import { Component, OnInit, inject, PLATFORM_ID, ChangeDetectionStrategy, signal, effect, untracked } from '@angular/core';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, observeOn, filter } from 'rxjs/operators';
import { asyncScheduler } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router, NavigationEnd } from '@angular/router';

@Component({
   selector: 'app-download-prompt',
   templateUrl: './download-prompt.component.html',
   styleUrls: ['./download-prompt.component.scss'],
   standalone: true,
   imports: [NgOptimizedImage],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class DownloadPromptComponent implements OnInit {
   private readonly observer = inject(BreakpointObserver);
   private readonly platformId = inject(PLATFORM_ID);
   private readonly authService = inject(AuthService);
   private readonly router = inject(Router);

   private readonly isBrowser = isPlatformBrowser(this.platformId);

   // Signals
   readonly isOpen = signal(false);

   readonly isMobile = toSignal(
      this.observer.observe('(max-width: 767px)').pipe(
         observeOn(asyncScheduler),
         map(result => result.matches)
      ),
      { initialValue: false }
   );

   // Requirement: For logged-in users
   readonly isLoggedIn = toSignal(
      this.authService.isLoggedIn$.pipe(observeOn(asyncScheduler)),
      { initialValue: this.authService.isLoggedIn() }
   );

   // Tracks the current URL to avoid showing the prompt on activation/onboarding screens
   readonly currentUrl = toSignal(
      this.router.events.pipe(
         filter(event => event instanceof NavigationEnd),
         map(event => (event as NavigationEnd).urlAfterRedirects),
         observeOn(asyncScheduler)
      ),
      { initialValue: this.router.url }
   );

   private timeoutId: any;

   constructor() {
      if (this.isBrowser) {
         effect((onCleanup) => {
            const loggedIn = this.isLoggedIn();
            const mobile = this.isMobile();
            const url = this.currentUrl();
            const hasSeen = sessionStorage.getItem('hasSeenDownloadPrompt');

            // Exclude activation, onboarding, and specific informational routes
            const excludedRoutes = [
               '/authentication/',
               '/activate',
               '/supported-countries',
               '/supported-brokerages',
               '/investing-coverage'
            ];
            const isOnExcludedRoute = excludedRoutes.some(route => url.includes(route));

            // Clear any pending timeout if conditions change
            if (this.timeoutId) {
               clearTimeout(this.timeoutId);
            }

            if (loggedIn && mobile && !isOnExcludedRoute && !hasSeen && !untracked(this.isOpen)) {
               this.timeoutId = setTimeout(() => {
                  // Re-verify conditions after delay in a non-reactive way
                  const stillValid = untracked(() => {
                     const currentUrl = this.currentUrl();
                     const isStillExcluded = excludedRoutes.some(route => currentUrl.includes(route));
                     return this.isLoggedIn() && 
                            this.isMobile() && 
                            !isStillExcluded &&
                            !sessionStorage.getItem('hasSeenDownloadPrompt');
                  });

                  if (stillValid) {
                     this.isOpen.set(true);
                  }
               }, 3000); // 3s delay for best UX performance
            }

            onCleanup(() => {
               if (this.timeoutId) {
                  clearTimeout(this.timeoutId);
               }
            });
         }, { allowSignalWrites: true });
      }
   }

   ngOnInit(): void {}

   dismissPrompt() {
      this.isOpen.set(false);
      if (this.isBrowser) {
         sessionStorage.setItem('hasSeenDownloadPrompt', 'true');
      }
   }

   openDownloadPrompt() {
      if (this.isMobile()) {
         this.isOpen.set(true);
      }
   }

   redirectToAppStore() {
      if (this.isBrowser) {
         this.dismissPrompt();
         window.location.href = 'https://app.musaffa.com/ar0PDlRM2Jb';
      }
   }
}
